import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from "@apollo/client";
import { userSelector } from "helper/security";
import { useDispatch } from "react-redux";
import yup from "validation";
import { EVICT_INVOICE_QUERIES, INVOICE_APPROVE_CLASSIC_CANCELLATION_HOTEL_MUTATION, REFETCH_INVOICE_QUERIES } from "./gql";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EOfferVersionContentBlockType, EOfferVersionStatus, ViewInvoiceQuery } from "__generated__/graphql";
import { QuickPriceCalculatorTypeLister } from "semshared/pricelist/quickprice_lister";
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import i18next from 'i18next';
import { Helmet } from 'react-helmet';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DownloadIcon from '@mui/icons-material/Download';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import { formatPercentage } from 'components/Percentage';
import { formatOfferDay } from 'semshared/utils/format';
import { formatDate } from 'components/DateTime';
import moment from 'moment';
import { formatPrice } from 'components/Price';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { useNavigate } from 'react-router-dom';
import { getFullBackendUrl } from 'helper/download';


interface InvoiceCancellationFormProps {
  offer: NonNullable<ViewInvoiceQuery['viewWlOffer']>;
  version: NonNullable<ViewInvoiceQuery['viewWlOfferInvoiceVersion']>;
  downloadVersion?: ViewInvoiceQuery['viewWlOfferInvoiceDownloadVersion'];
  event: ViewInvoiceQuery['viewWlOfferInvoiceEvent'];
  quickPriceCalculator: QuickPriceCalculatorTypeLister;
}

const validationSchemaCancellationHotel = yup.object().shape({
  agreedCancellationWaiveNet: yup.number(),
  manualCancellationWaiveNet: yup
    .number()
    .nullable()
    .min(0, () => i18next.t('invoice-cancellation-manualwaive-min-error'))
    .max(yup.ref('totalPriceNet'), () => i18next.t('invoice-cancellation-manualwaive-max-error')),
  totalPriceNet: yup.number().required(),
  totalPriceGross: yup.number().required(),
});
export type InvoiceCancellationFormHotelType = yup.InferType<typeof validationSchemaCancellationHotel>;


export default function InvoiceCancellationFormHotel(props: InvoiceCancellationFormProps & { onProcessStep: () => void; }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [approveCancellationInvoice, { loading: approveCancellationInvoiceLoading }] = useMutation(INVOICE_APPROVE_CLASSIC_CANCELLATION_HOTEL_MUTATION);

  const [canApprove] = useState(props.version.status === EOfferVersionStatus.CIV_WAITFORHOTEL);
  const [canReject] = useState(props.version.status === EOfferVersionStatus.CIV_WAITFORHOTEL);

  const toFormSchema = (offer: typeof props.offer, version: typeof props.version): InvoiceCancellationFormHotelType => {
    return {
      agreedCancellationWaiveNet: version.agreedCancellationWaiveNet || 0,
      manualCancellationWaiveNet: version.manualCancellationWaiveNet,
      totalPriceNet: version.totalPriceNet,
      totalPriceGross: version.totalPriceGross,
    };
  }

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchemaCancellationHotel) as any,
    defaultValues: toFormSchema(props.offer, props.version),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors: validationErrors, isValid, isDirty, isValidating, isSubmitting },
  } = form;

  const onSubmitApprove = async (values: InvoiceCancellationFormHotelType) => {
    if (!canApprove) return;

    try {
      const res = await approveCancellationInvoice({
        variables: {
          offerVersionId: props.version.id,
          manualCancellationWaiveNet: values.manualCancellationWaiveNet,
        },
        update: cache => {
          EVICT_INVOICE_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_INVOICE_QUERIES(props.offer.id, props.version.id),
      });
      reset(toFormSchema(props.offer, res.data!.approveClassicCancellationInvoiceHotel));
      dispatchMessage(dispatch, i18next.t('cancellation-invoice-status-approved'));
      props.onProcessStep();
      return res.data!.approveClassicCancellationInvoiceHotel.id;
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };
  const onSubmitPrintProforma = async (values: InvoiceCancellationFormHotelType) => {
    window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.version.id}`), '_blank');
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`invoices-list-page-title-${props.offer.source}`), props.offer])}</title>
      </Helmet>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {props.version.extRefCode || props.offer.refCode} - {props.offer.bookingTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.offer.status === 'APPROVED'
              ? i18next.t('enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION')
              : i18next.t(`enums-EOfferVersionStatus-${props.version.status}`)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {canApprove && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              confirmationQuestion={i18next.t('cancellation-invoice-confirm-question')}
              confirmationTitle={i18next.t('cancellation-invoice-confirm-question-title')}
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbUpIcon />}
              disabled={!isValid || isSubmitting}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  await handleSubmit(onSubmitApprove)();
                  navigate('/offers/lister');
                }
              }}
            >
              {i18next.t('cancellation-invoice-confirm-title')}
            </ConfirmationButton>
          )}
          {props.downloadVersion && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={isSubmitting}
              onClick={() => {
                window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.downloadVersion!.id}`), '_blank');
              }}
            >
              {props.downloadVersion.status === EOfferVersionStatus.CANCELLED_CLIENT
                ? i18next.t('offer-cancellation-confirmation-process-download')
                : i18next.t('offer-confirmation-process-download')}
            </Button>
          )}
          {!canApprove && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={!isValid || isSubmitting}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmitPrintProforma)();
                }
              }}
            >
              {i18next.t('invoices-download-proforma-invoice')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <UnsavedChangesPrompt isDirty={isDirty} />
          <FormProvider {...form}>
            <Grid container spacing={2}>
              {props.version.contentBlocks.map((cb, cbIndex) => (
                cb.type === EOfferVersionContentBlockType.CANCELLATION_ITEMS && cb.lineItems.length > 0 && (
                  <Grid item xs={12} key={`cancellationitems_${cbIndex}`}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                          {i18next.t('offerblock-cancellationitems-header-phase', {
                            rate: cb.cancellationRule ? formatPercentage(cb.cancellationRule.rate, 0) : '?',
                            daysToEvent: cb.cancellationRule ? cb.cancellationRule.daysToEvent : '?',
                          })}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container>
                          {cb.lineItems.map((li, liIndex) => (
                            <React.Fragment key={liIndex}>
                              <Grid item sm={2}>
                                {formatOfferDay(li.day)} - {formatDate(moment.utc(props.version.startDate, 'YYYY-MM-DD').add(li.day, 'days').toDate())}
                              </Grid>
                              <Grid item sm={1}>
                                {li.count}
                              </Grid>
                              <Grid item sm={8}>
                                <Typography>
                                  {li.header} ({li.sku})
                                </Typography>
                              </Grid>
                              <Grid item sm={1}>
                                {formatPrice(li.priceGross, props.version.priceList.currency)}
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )
              ))}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Grid container>
                    <Grid item xs={12} sm={2}>
                      <FormInputCurrency
                        name={'agreedCancellationWaiveNet'}
                        label={i18next.t('invoice-cancellation-agreedwaive')}
                        currency={props.version.priceList.currency}
                        control={control}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormInputCurrency
                        name={'manualCancellationWaiveNet'}
                        label={i18next.t('invoice-cancellation-manualwaive')}
                        currency={props.version.priceList.currency}
                        control={control}
                        disabled={!canApprove || isSubmitting}
                        textFieldProps={{
                          inputProps: {
                            step: '0.01',
                            inputMode: 'decimal',
                            min: 0,
                            max: watch('totalPriceNet'),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {canApprove && (
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setValue('manualCancellationWaiveNet', getValues('totalPriceNet'));
                          }}
                        >
                          {i18next.t('invoice-cancellation-full-waive-button')}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <hr />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">{i18next.t('invoice-totals')}</Typography>
              </Grid>
              <Grid item xs={12}>
                {i18next.t('invoice-totals-net')}: {formatPrice(Math.max(watch('totalPriceNet') - (watch('manualCancellationWaiveNet') || 0), 0), props.version.priceList.currency)}
              </Grid>
              <Grid item xs={12}>
                {i18next.t('invoice-totals-taxes')}: {formatPrice(Math.max(watch('totalPriceGross') - watch('totalPriceNet') - (watch('manualCancellationWaiveNet') || 0), 0), props.version.priceList.currency)}
              </Grid>
              <Grid item xs={12}>
                {i18next.t('invoice-totals-gross')}: {formatPrice(Math.max(watch('totalPriceGross') - (watch('manualCancellationWaiveNet') || 0), 0), props.version.priceList.currency)}
              </Grid>
            </Grid>
          </FormProvider>
        </Grid>
      </Grid>
    </>
  );
}
